import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { APIProvider } from '@vis.gl/react-google-maps';
import AddressAutocomplete from '../components/clients/AddressAutocomplete';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { DateInput } from '../components/ui/date-input';
import { toast } from 'sonner';
import { calcolaCodiceFiscale } from '../utils/fiscalCode';
import {
  Plus, Search, MoreHorizontal, FileCheck, Upload, Trash2, Pencil,
  FileText, ChevronLeft, ChevronRight, Sparkles, Loader2, UserPlus, Calculator,
  ArrowUp, ArrowDown, Send, Mail, Check, X, Settings, RefreshCw, IdCard, AlertTriangle,
} from 'lucide-react';
import { emptyClient, NAZIONI } from '../constants/clientConstants';
import ClientCompletionBanner from '../components/clients/ClientCompletionBanner';
import { DelegaGeneraleBadge } from '../components/DelegaGenerale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DEFAULT_RECIPIENT = 'comune.prato@postacert.toscana.it';

// Mappa il tipo documento scelto nel form sul document_type richiesto da /api/ocr/extract
const OCR_DOC_TYPE_MAP = {
  "Carta d'identità elettronica": 'carta_identita',
  'Patente': 'patente',
  'Passaporto': 'passaporto',
  'Permesso di soggiorno': 'permesso_soggiorno',
};

// Sigla con cui il documento caricato viene salvato tra i "Documenti anagrafici" del cliente
const DOC_LABEL_MAP = {
  "Carta d'identità elettronica": 'CIE',
  'Patente': 'PAT',
  'Passaporto': 'PASS',
  'Permesso di soggiorno': 'PSE',
};

const DOC_TIPO_OPTIONS = [
  "Carta d'identità elettronica",
  'Patente',
  'Passaporto',
  'Permesso di soggiorno',
  'Altro',
];

const QUALITA_OPTIONS = [
  { value: 'madre', label: 'Madre' },
  { value: 'padre', label: 'Padre' },
  { value: 'affidatario', label: 'Affidatario' },
  { value: 'adottante', label: 'Adottante' },
  { value: 'legale_rappresentante', label: 'Legale rappresentante' },
];

const PAGAMENTO_OPTIONS = [
  { value: 'cc_bancario', label: 'Conto corrente bancario' },
  { value: 'cc_postale', label: 'Conto corrente postale' },
  { value: 'libretto_postale', label: 'Libretto postale' },
];

const emptyForm = {
  client_id: '',
  doc_tipo: "Carta d'identità elettronica",
  doc_numero: '',
  doc_ente: '',
  cittadinanza: '',
  extracomunitaria: false,
  qualita: 'madre',
  bambino_nome: '',
  bambino_data_nascita: '',
  dich_nessun_trattamento: true,
  dich_trattamento_inferiore: false,
  trattamento_ente: '',
  trattamento_importo: '',
  dich_richiesta_presentata: false,
  richiesta_ente: '',
  dich_cessato_lavoro: false,
  cessazione_data: '',
  datore_lavoro: '',
  qualifica: '',
  pagamento: 'cc_bancario',
  istituto: '',
  filiale: '',
  iban: '',
  intestatario_conto: '',
  conto_cointestato: false,
  cointestatario_nome: '',
  data_domanda: '',
  notes: '',
};

const emptyPecConfig = {
  pec_email: '', pec_password: '', smtp_server: 'mail.postacert.it.net', smtp_port: 465,
  imap_server: 'mail.postacert.it.net', imap_port: 993, default_recipient: DEFAULT_RECIPIENT,
};

// Il cliente ha un permesso di soggiorno tra i documenti anagrafici?
const hasPse = (client) => (client?.documents || []).some(d => (d.description || '').toUpperCase().startsWith('PSE '));

const AssegnoMaternita = () => {
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null); // `${id}-firmato` | `${id}-isee`
  const [extractingDoc, setExtractingDoc] = useState(false);
  const [newClientMode, setNewClientMode] = useState(false);
  const [newClientData, setNewClientData] = useState(emptyClient);
  const [creatingClient, setCreatingClient] = useState(false);
  // Documento caricato mentre il cliente non esiste ancora: si allega in anagrafica dopo la creazione
  const [pendingIdentityDoc, setPendingIdentityDoc] = useState(null);

  // Invio PEC
  const [sendPecConfirm, setSendPecConfirm] = useState(null);
  const [pecPreview, setPecPreview] = useState({ url: '', loading: false, error: '' });
  const [sendCopyToClient, setSendCopyToClient] = useState(false);
  const [sendingPec, setSendingPec] = useState(null);
  const [fetchingRicevute, setFetchingRicevute] = useState(null);
  const [sendingRicevuteEmail, setSendingRicevuteEmail] = useState(null);

  // Configurazione PEC
  const [pecDialogOpen, setPecDialogOpen] = useState(false);
  const [pecConfig, setPecConfig] = useState(emptyPecConfig);
  const [pecPasswordSet, setPecPasswordSet] = useState(false);
  const [pecSaving, setPecSaving] = useState(false);
  const [pecTestRecipient, setPecTestRecipient] = useState('');
  const [pecTesting, setPecTesting] = useState(false);

  useEffect(() => { fetchClients(); }, []);

  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // Nome del cliente nel campo di ricerca anche quando viene abbinato da un documento caricato
  useEffect(() => {
    const c = clients.find(x => x.id === formData.client_id);
    if (c) setClientSearch(`${c.last_name} ${c.first_name}`);
  }, [formData.client_id, clients]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchItems(); }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, sortDirection]);

  // Anteprima della domanda firmata (quella che parte in PEC) nella conferma di invio
  useEffect(() => {
    if (!sendPecConfirm) return undefined;
    let blobUrl = '';
    setPecPreview({ url: '', loading: true, error: '' });
    axios.get(`${API}/assegno-maternita/${sendPecConfirm.id}/firmato`, { responseType: 'blob' })
      .then(res => {
        blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setPecPreview({ url: blobUrl, loading: false, error: '' });
      })
      .catch(() => setPecPreview({ url: '', loading: false, error: 'Domanda firmata non ancora caricata' }));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [sendPecConfirm]);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento dei clienti');
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', currentPage);
      params.append('page_size', ITEMS_PER_PAGE);
      params.append('sort_direction', sortDirection);
      const res = await axios.get(`${API}/assegno-maternita?${params.toString()}`);
      setItems(res.data.items);
      setTotalItems(res.data.total);
    } catch (e) {
      toast.error('Errore nel caricamento delle pratiche');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const q = clientSearch.toLowerCase();
    const cognomeNome = `${c.last_name} ${c.first_name}`.toLowerCase();
    const nomeCognome = `${c.first_name} ${c.last_name}`.toLowerCase();
    return cognomeNome.includes(q) || nomeCognome.includes(q) || (c.fiscal_code || '').toLowerCase().includes(q);
  });

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const toggleSortDirection = () => {
    setCurrentPage(1);
    setSortDirection(d => (d === 'desc' ? 'asc' : 'desc'));
  };

  const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // Selezione cliente: se ha già un permesso di soggiorno in anagrafica è extracomunitaria
  const selectClient = (client) => {
    setFormData(prev => ({ ...prev, client_id: client.id, extracomunitaria: prev.extracomunitaria || hasPse(client) }));
  };

  const openNew = () => {
    setEditingRecord(null);
    setFormData({ ...emptyForm, data_domanda: todayIso() });
    setClientSearch('');
    setNewClientMode(false);
    setNewClientData({ ...emptyClient, gender: 'F' });
    setPendingIdentityDoc(null);
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setNewClientMode(false);
    setNewClientData(emptyClient);
    setPendingIdentityDoc(null);
    const data = {};
    Object.keys(emptyForm).forEach(k => {
      data[k] = record[k] !== undefined && record[k] !== null ? record[k] : emptyForm[k];
    });
    setFormData(data);
    setClientSearch('');
    setDialogOpen(true);
  };

  const matchClientByCf = (cf) => {
    if (!cf) return null;
    return clients.find(c => (c.fiscal_code || '').toUpperCase() === cf.toUpperCase()) || null;
  };

  const handleCalcolaCfDaDati = async () => {
    try {
      const cf = await calcolaCodiceFiscale(newClientData);
      setNewClientData(prev => ({ ...prev, fiscal_code: cf }));
      const existing = matchClientByCf(cf);
      if (existing) {
        toast.warning(`Attenzione: CF già registrato in anagrafica (${existing.last_name} ${existing.first_name})`);
      } else {
        toast.success('Codice Fiscale calcolato');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Allega il file ai "Documenti anagrafici" del cliente come "SIGLA COGNOME NOME"; un documento
  // dello stesso tipo già presente viene sostituito (regola comune a tutti i moduli Servizi).
  const uploadClientDocument = async (clientId, file, shortLabel, lastName, firstName, expiryDate) => {
    try {
      const label = `${shortLabel} ${lastName || ''} ${firstName || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
      try {
        const clientRes = await axios.get(`${API}/clients/${clientId}`);
        const existing = (clientRes.data.documents || []).find(
          (d) => d.category === 'anagrafico' && (d.description || '').toUpperCase().startsWith(`${shortLabel} `)
        );
        if (existing) {
          await axios.delete(`${API}/clients/${clientId}/documents/${existing.id}`);
        }
      } catch (_) {
        // non bloccare l'upload se il controllo duplicati fallisce
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', label);
      fd.append('category', 'anagrafico');
      if (expiryDate) fd.append('expiry_date', expiryDate);
      await axios.post(`${API}/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (error) {
      toast.error(`Dati estratti, ma il documento non è stato allegato in anagrafica: ${error.response?.data?.detail || 'errore'}`);
    }
  };

  const handleExtractIdentityDoc = async (file) => {
    if (!file) return;
    const ocrType = OCR_DOC_TYPE_MAP[formData.doc_tipo];
    if (!ocrType) {
      toast.error('Estrazione automatica non disponibile per "Altro" — seleziona il tipo documento corretto o inserisci i dati a mano');
      return;
    }
    const docLabel = DOC_LABEL_MAP[formData.doc_tipo];
    const isPse = docLabel === 'PSE';
    setExtractingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', ocrType);
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const c = res.data.client_data || {};
      const numero = d.document_number || d.license_number || '';
      const ente = d.issuing_authority || '';
      const cittad = d.nationality_specific || d.nationality || '';
      const expiryDate = d.expiry_date || '';
      if (!numero && !ente && !cittad && !c.fiscal_code && !c.first_name) {
        toast.error('Nessun dato riconosciuto nel documento — verifica di aver scelto il "Tipo documento" corretto sopra prima di ricaricarlo');
        return;
      }
      // Permesso di soggiorno o cittadinanza extra UE → allegato PSE obbligatorio nella PEC
      const extra = isPse || (d.nationality || '').toUpperCase() === 'EXTRA UE';
      setFormData(prev => ({
        ...prev,
        // il numero/ente del documento d'identità non va sovrascritto da quello del permesso
        doc_numero: isPse && prev.doc_numero ? prev.doc_numero : (numero || prev.doc_numero),
        doc_ente: isPse && prev.doc_ente ? prev.doc_ente : (ente || prev.doc_ente),
        cittadinanza: (cittad || prev.cittadinanza || '').toUpperCase(),
        extracomunitaria: prev.extracomunitaria || extra,
      }));

      const match = matchClientByCf(c.fiscal_code);
      if (match) {
        setNewClientMode(false);
        selectClient(match);
        await uploadClientDocument(match.id, file, docLabel, match.last_name, match.first_name, expiryDate);
        toast.success(`Cliente esistente abbinato: ${match.first_name} ${match.last_name} — documento allegato in anagrafica`);
        fetchClients();
        return;
      }

      let merged = {
        ...newClientData,
        first_name: c.first_name || newClientData.first_name,
        last_name: c.last_name || newClientData.last_name,
        fiscal_code: c.fiscal_code || newClientData.fiscal_code,
        gender: c.gender || newClientData.gender,
        birth_date: c.birth_date || newClientData.birth_date,
        birth_abroad: c.birth_abroad !== undefined ? c.birth_abroad : newClientData.birth_abroad,
        birth_comune: c.birth_comune || (c.birth_abroad ? '' : newClientData.birth_comune),
        birth_provincia: c.birth_provincia || newClientData.birth_provincia,
        birth_country: c.birth_country || newClientData.birth_country,
        address: c.address || newClientData.address,
        address_number: c.address_number || newClientData.address_number,
        city: c.city || newClientData.city,
        city_provincia: c.city_provincia || newClientData.city_provincia,
      };

      if (!merged.postal_code && merged.city && merged.city_provincia) {
        try {
          const capRes = await axios.get(`${API}/cap`, { params: { city: merged.city, provincia: merged.city_provincia } });
          if (capRes.data.single && capRes.data.auto_cap) merged = { ...merged, postal_code: capRes.data.auto_cap };
        } catch (e) {
          // il CAP è un aiuto in più
        }
      }

      const canCalcCf = !merged.fiscal_code && merged.last_name && merged.first_name && merged.gender && merged.birth_date &&
        ((!merged.birth_abroad && merged.birth_comune && merged.birth_provincia) || (merged.birth_abroad && merged.birth_country));
      if (canCalcCf) {
        try {
          const cfRes = await axios.post(`${API}/calculate-fiscal-code`, {
            surname: merged.last_name,
            name: merged.first_name,
            birth_date: merged.birth_date,
            gender: merged.gender,
            birth_abroad: merged.birth_abroad,
            birth_comune: merged.birth_comune,
            birth_provincia: merged.birth_provincia,
            birth_country: merged.birth_country,
          });
          if (cfRes.data.fiscal_code) merged = { ...merged, fiscal_code: cfRes.data.fiscal_code };
        } catch (e) {
          // il calcolo CF è un aiuto in più
        }
      }

      const matchAfterCalc = canCalcCf ? matchClientByCf(merged.fiscal_code) : null;
      if (matchAfterCalc) {
        setNewClientMode(false);
        selectClient(matchAfterCalc);
        await uploadClientDocument(matchAfterCalc.id, file, docLabel, matchAfterCalc.last_name, matchAfterCalc.first_name, expiryDate);
        toast.success(`Cliente esistente abbinato: ${matchAfterCalc.first_name} ${matchAfterCalc.last_name} — documento allegato in anagrafica`);
        fetchClients();
      } else {
        setNewClientMode(true);
        // più documenti prima del salvataggio (es. carta d'identità + permesso): si allegano tutti
        setPendingIdentityDoc(prev => [...(prev || []).filter(p => p.label !== docLabel), { file, label: docLabel, expiryDate }]);
        setNewClientData(merged);
        toast.success('Nuovo cliente rilevato — dati precompilati, rivedi e salva (il documento si allega automaticamente)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'estrazione dati dal documento');
    } finally {
      setExtractingDoc(false);
    }
  };

  const handleCalcolaCfNewClient = async () => {
    const cf = (newClientData.fiscal_code || '').trim().toUpperCase();
    if (cf.length < 16) {
      toast.error('Codice fiscale non valido (servono 16 caratteri)');
      return;
    }
    try {
      const res = await axios.post(`${API}/decode-fiscal-code`, { fiscal_code: cf });
      const bd = res.data.birth_date || '';
      const birthDateIso = /^\d{4}-\d{2}-\d{2}$/.test(bd)
        ? bd
        : (() => { const p = bd.split('/'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : ''; })();
      setNewClientData(prev => ({
        ...prev,
        gender: res.data.gender || prev.gender,
        birth_date: birthDateIso || prev.birth_date,
        ...(res.data.birth_abroad !== undefined ? {
          birth_abroad: res.data.birth_abroad,
          birth_country: res.data.birth_country || '',
          birth_comune: res.data.birth_comune || '',
          birth_provincia: res.data.birth_provincia || '',
        } : {}),
      }));
      toast.success('Sesso, data e luogo di nascita calcolati dal Codice Fiscale');
    } catch (error) {
      toast.error('Impossibile calcolare i dati dal Codice Fiscale');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let clientId = formData.client_id;

    if (newClientMode) {
      if (!newClientData.last_name || !newClientData.first_name) {
        toast.error('Compila almeno Cognome e Nome del nuovo cliente');
        return;
      }
      if (!newClientData.birth_abroad && (!newClientData.birth_comune || !newClientData.birth_provincia)) {
        toast.error('Compila Comune e Provincia di nascita (o attiva "Nato/a all\'estero")');
        return;
      }
      if (newClientData.birth_abroad && !newClientData.birth_country) {
        toast.error('Seleziona la Nazione di nascita');
        return;
      }
      setCreatingClient(true);
      try {
        const res = await axios.post(`${API}/clients`, newClientData);
        clientId = res.data.id;
        for (const doc of pendingIdentityDoc || []) {
          await uploadClientDocument(clientId, doc.file, doc.label, newClientData.last_name, newClientData.first_name, doc.expiryDate);
        }
        setPendingIdentityDoc(null);
        toast.success('Nuovo cliente registrato in anagrafica');
        fetchClients();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nella registrazione del nuovo cliente');
        setCreatingClient(false);
        return;
      }
      setCreatingClient(false);
    }

    if (!clientId) {
      toast.error('Seleziona il cliente');
      return;
    }
    if (formData.dich_cessato_lavoro && !formData.cessazione_data) {
      toast.error('Indica la data di cessazione del lavoro');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, client_id: clientId };
      if (editingRecord) {
        await axios.put(`${API}/assegno-maternita/${editingRecord.id}`, payload);
        toast.success('Pratica aggiornata');
      } else {
        await axios.post(`${API}/assegno-maternita`, payload);
        toast.success('Pratica creata — stampa la domanda, falla firmare e carica la scansione');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Eliminare la pratica di ${record.client_name}?`)) return;
    try {
      await axios.delete(`${API}/assegno-maternita/${record.id}`);
      toast.success('Pratica eliminata');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const handleUpload = async (record, kind, file) => {
    if (!file) return;
    setUploadingFor(`${record.id}-${kind}`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/assegno-maternita/${record.id}/upload-${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(kind === 'firmato' ? 'Domanda firmata caricata' : 'Attestazione ISEE caricata');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento del file');
    } finally {
      setUploadingFor(null);
    }
  };

  const openPdf = async (url) => {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: res.data.type || 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (error) {
      toast.error('Documento non disponibile');
    }
  };

  // ============ PEC ============
  const handleSendPec = async (record) => {
    setSendingPec(record.id);
    try {
      const res = await axios.post(`${API}/assegno-maternita/${record.id}/send-pec`, {
        send_copy_to_client: sendCopyToClient,
      });
      toast.success(res.data.message || 'PEC inviata');
      if (sendCopyToClient) {
        if (res.data.copy_sent_to_client) toast.success('Copia email inviata al cliente');
        else if (res.data.copy_error) toast.error(`Copia email al cliente non inviata: ${res.data.copy_error}`);
      }
      fetchItems();
      // Auto-aggiornamento silenzioso delle ricevute PEC 6s dopo l'invio (come Canone RAI)
      setTimeout(() => {
        axios.post(`${API}/assegno-maternita/fetch-ricevute-all?limit=10`).then(() => fetchItems()).catch(() => {});
      }, 6000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della PEC');
    } finally {
      setSendingPec(null);
    }
  };

  const fetchRicevute = async (record) => {
    setFetchingRicevute(record.id);
    try {
      toast.loading('Scarico ricevute PEC dalla casella IMAP...', { id: `fetch-${record.id}` });
      const res = await axios.post(`${API}/assegno-maternita/${record.id}/fetch-ricevute`);
      const found = res.data.found || [];
      if (found.length === 0) {
        toast.info('Nessuna nuova ricevuta trovata (potrebbe non essere ancora arrivata)', { id: `fetch-${record.id}` });
      } else {
        toast.success(`Trovate: ${found.join(' + ')}`, { id: `fetch-${record.id}` });
      }
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore IMAP', { id: `fetch-${record.id}` });
    } finally {
      setFetchingRicevute(null);
    }
  };

  const fetchAllRicevute = async () => {
    toast.loading('Scarico le ricevute PEC mancanti...', { id: 'fetch-all' });
    try {
      const res = await axios.post(`${API}/assegno-maternita/fetch-ricevute-all`);
      toast.success(res.data.processed ? `Controllate ${res.data.processed} pratiche, aggiornate ${res.data.updated}` : res.data.message, { id: 'fetch-all' });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore IMAP', { id: 'fetch-all' });
    }
  };

  const handleSendRicevuteEmail = async (record) => {
    setSendingRicevuteEmail(record.id);
    try {
      const res = await axios.post(`${API}/assegno-maternita/${record.id}/send-ricevute-email`);
      toast.success(res.data.message || 'Ricevute inviate al cliente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio delle ricevute al cliente');
    } finally {
      setSendingRicevuteEmail(null);
    }
  };

  const openPecConfig = async () => {
    try {
      const res = await axios.get(`${API}/assegno-maternita/pec-config`);
      setPecConfig({ ...emptyPecConfig, ...res.data, pec_password: '' });
      setPecPasswordSet(!!res.data.pec_password_set);
      if (!pecTestRecipient && res.data.pec_email) setPecTestRecipient(res.data.pec_email);
      setPecDialogOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento configurazione PEC');
    }
  };

  const savePecConfig = async () => {
    setPecSaving(true);
    try {
      const payload = { ...pecConfig, smtp_port: parseInt(pecConfig.smtp_port) || 465, imap_port: parseInt(pecConfig.imap_port) || 993 };
      if (!payload.pec_password) delete payload.pec_password;
      delete payload.pec_password_set;
      await axios.put(`${API}/assegno-maternita/pec-config`, payload);
      toast.success('Configurazione PEC salvata');
      setPecDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setPecSaving(false);
    }
  };

  const testPecConfig = async () => {
    setPecTesting(true);
    try {
      const res = await axios.post(`${API}/assegno-maternita/pec-config/test`, { test_recipient: pecTestRecipient.trim() });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel test');
    } finally {
      setPecTesting(false);
    }
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  // Cella "carica / caricato" per domanda firmata e ISEE
  const uploadCell = (record, kind, filename, originalName, viewUrl) => {
    const busy = uploadingFor === `${record.id}-${kind}`;
    const input = (
      <input
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        className="hidden"
        disabled={busy}
        onChange={(e) => { handleUpload(record, kind, e.target.files?.[0]); e.target.value = ''; }}
      />
    );
    if (filename) {
      return (
        <div className="flex items-center">
          <button type="button" onClick={() => openPdf(viewUrl)} title={originalName}>
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
              <FileCheck className="w-3 h-3 mr-1" />Caricato
            </Badge>
          </button>
          <label className="ml-1.5 inline-flex items-center text-slate-400 hover:text-slate-700 cursor-pointer" title="Sostituisci">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {input}
          </label>
        </div>
      );
    }
    return (
      <label className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 cursor-pointer border border-dashed border-slate-300 rounded-full px-2.5 py-1">
        {busy ? 'Caricamento...' : <><Upload className="w-3 h-3" /> Carica</>}
        {input}
      </label>
    );
  };

  const ricevuteBadges = (record) => (
    <div className="flex gap-1 flex-wrap items-center">
      {record.ricevuta_accettazione ? (
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] px-1.5 py-0" title={`Accettata: ${record.ricevuta_accettazione.date || ''}`}>
          <Check className="w-2.5 h-2.5 mr-0.5" /> Accett.
        </Badge>
      ) : (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400">Accett. attesa</Badge>
      )}
      {record.ricevuta_mancata_consegna ? (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 text-[10px] px-1.5 py-0" title={`Mancata consegna: ${record.ricevuta_mancata_consegna.date || ''}`}>
          <X className="w-2.5 h-2.5 mr-0.5" /> Mancata cons.
        </Badge>
      ) : record.ricevuta_consegna ? (
        <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 text-[10px] px-1.5 py-0" title={`Consegnata: ${record.ricevuta_consegna.date || ''}`}>
          <Check className="w-2.5 h-2.5 mr-0.5" /> Cons.
        </Badge>
      ) : (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-slate-400">Cons. attesa</Badge>
      )}
      {(record.ricevuta_accettazione || record.ricevuta_consegna || record.ricevuta_mancata_consegna) && (
        <button
          onClick={() => openPdf(`${API}/assegno-maternita/${record.id}/ricevute-pdf`)}
          className="ml-1 p-0.5 hover:bg-indigo-50 rounded text-indigo-700 border border-indigo-200"
          title="Anteprima ricevute PEC (PDF unico)"
        ><FileText className="w-3 h-3" /></button>
      )}
    </div>
  );

  const selectedClient = newClientMode ? null : clients.find(c => c.id === formData.client_id);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="assegno-maternita-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Assegno Maternità
          </h1>
          <p className="text-slate-600 mt-1">Domanda di assegno di maternità al Comune di Prato (art. 74 D.Lgs 151/2001) — invio via PEC</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={fetchAllRicevute}>
            <RefreshCw className="w-4 h-4 mr-2" />Scarica ricevute PEC
          </Button>
          <Button variant="outline" onClick={openPecConfig}>
            <Settings className="w-4 h-4 mr-2" />Configura PEC
          </Button>
          <Button onClick={openNew} data-testid="new-assegno-maternita-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuova Pratica
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per cliente, codice fiscale o nome del bambino..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : totalItems === 0 ? (
            <div className="p-8 text-center text-slate-500">Nessuna pratica trovata</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Richiedente</TableHead>
                    <TableHead className="font-semibold">Bambino/a</TableHead>
                    <TableHead className="font-semibold">
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-slate-900"
                        onClick={toggleSortDirection}
                        title={sortDirection === 'desc' ? 'Più recenti prima' : 'Meno recenti prima'}
                      >
                        Data Caricamento
                        {sortDirection === 'desc' ? <ArrowDown className="w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5" />}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold">Domanda</TableHead>
                    <TableHead className="font-semibold">Firmata</TableHead>
                    <TableHead className="font-semibold">ISEE</TableHead>
                    <TableHead className="font-semibold">PEC</TableHead>
                    <TableHead className="font-semibold">Ricevute al cliente</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="cursor-pointer hover:bg-slate-50" onClick={() => openEdit(record)}>
                        <div className="font-medium text-slate-900">{record.client_name || '-'}</div>
                        <div className="text-xs text-slate-500 font-mono">{record.client_fiscal_code}</div>
                        <div className="mt-1"><DelegaGeneraleBadge clientId={record.client_id} clientName={record.client_name} /></div>
                        {!record.pec_sent && record.allegati_mancanti?.length > 0 && (
                          <div className="text-[11px] text-amber-700 mt-0.5 flex items-start gap-1" title={`Manca: ${record.allegati_mancanti.join(', ')}`}>
                            <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                            Manca: {record.allegati_mancanti.join(', ')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        <div>{record.bambino_nome || '-'}</div>
                        {record.bambino_data_nascita && (
                          <div className="text-xs text-slate-400">nato/a il {new Date(record.bambino_data_nascita).toLocaleDateString('it-IT')}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{formatDateTime(record.created_at)}</TableCell>
                      <TableCell>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => openPdf(`${API}/assegno-maternita/${record.id}/domanda-pdf`)}
                          title="Domanda compilata da stampare e far firmare"
                        >
                          <FileText className="w-3.5 h-3.5 mr-1" />Stampa
                        </Button>
                      </TableCell>
                      <TableCell>
                        {uploadCell(record, 'firmato', record.signed_pdf_filename, record.signed_pdf_original_name,
                          `${API}/assegno-maternita/${record.id}/firmato`)}
                      </TableCell>
                      <TableCell>
                        {uploadCell(record, 'isee', record.isee_filename, record.isee_original_name,
                          `${API}/assegno-maternita/${record.id}/isee`)}
                      </TableCell>
                      <TableCell>
                        {record.pec_sent ? (
                          <div className="flex flex-col gap-1">
                            <Badge className="bg-green-100 text-green-700 w-fit" title={record.pec_recipient}>
                              <Mail className="w-3 h-3 mr-1" />Inviata {record.pec_sent_date ? new Date(record.pec_sent_date).toLocaleDateString('it-IT') : ''}
                            </Badge>
                            {ricevuteBadges(record)}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">Non inviata</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {(record.ricevuta_accettazione || record.ricevuta_consegna) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendRicevuteEmail(record)}
                            disabled={sendingRicevuteEmail === record.id}
                            className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          >
                            {sendingRicevuteEmail === record.id
                              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                              : <Mail className="w-4 h-4 mr-1.5" />}
                            Invia al cliente
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(record)}>
                              <Pencil className="w-4 h-4 mr-2" />Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openPdf(`${API}/documents/${record.documento_doc_id}`)}
                              disabled={!record.documento_doc_id}
                            >
                              <IdCard className="w-4 h-4 mr-2" />Documento d'identità
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openPdf(`${API}/documents/${record.pse_doc_id}`)}
                              disabled={!record.pse_doc_id}
                            >
                              <IdCard className="w-4 h-4 mr-2" />Permesso di soggiorno
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => { setSendCopyToClient(false); setSendPecConfirm(record); }}
                              disabled={sendingPec === record.id}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              {sendingPec === record.id ? 'Invio in corso...' : record.pec_sent ? 'Reinvia PEC' : 'Invia PEC'}
                            </DropdownMenuItem>
                            {record.pec_sent && (
                              <>
                                <DropdownMenuItem onClick={() => fetchRicevute(record)} disabled={fetchingRicevute === record.id}>
                                  {fetchingRicevute === record.id
                                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    : <Mail className="w-4 h-4 mr-2" />}
                                  Aggiorna ricevute PEC
                                </DropdownMenuItem>
                                {['accettazione', 'consegna', 'mancata_consegna'].filter(t => record[`ricevuta_${t}`]).map(t => (
                                  <DropdownMenuItem key={t} onClick={() => openPdf(`${API}/assegno-maternita/${record.id}/ricevuta/${t}/original`)}>
                                    <FileText className="w-4 h-4 mr-2" />Ricevuta {t.replace('_', ' ')} (originale)
                                  </DropdownMenuItem>
                                ))}
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(record)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} pratiche
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">Pagina {currentPage} di {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Modifica Pratica' : 'Nuova Pratica Assegno Maternità'}</DialogTitle>
            <DialogDescription>
              I dati anagrafici del richiedente (nascita, residenza, telefono, CF) vengono presi
              dall'anagrafica per compilare la domanda del Comune.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-2">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-violet-800 font-medium text-sm">
                <Sparkles className="w-4 h-4" />
                Estrazione automatica dati (facoltativa)
              </div>
              <p className="text-xs text-violet-700">
                Carica il documento d'identità (e, per le cittadine extracomunitarie, anche il permesso
                di soggiorno): i campi si compilano da soli e i documenti vengono salvati in anagrafica,
                da dove vengono allegati alla PEC.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs text-violet-700">Tipo documento che stai per caricare</Label>
                <Select value={formData.doc_tipo} onValueChange={(v) => set('doc_tipo', v)}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className={`flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2.5 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extractingDoc ? 'opacity-60 pointer-events-none' : ''}`}>
                {extractingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Carica {formData.doc_tipo}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => { handleExtractIdentityDoc(e.target.files?.[0]); e.target.value = ''; }}
                />
              </label>
            </div>

            <ClientCompletionBanner
              client={selectedClient}
              onSaved={(updated) => setClients(prev => prev.map(c => c.id === updated.id ? updated : c))}
            />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Richiedente</Label>
                {!editingRecord && (
                  <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs gap-1"
                    onClick={() => setNewClientMode(m => !m)}>
                    <UserPlus className="w-3.5 h-3.5" />
                    {newClientMode ? 'Seleziona cliente esistente' : 'Nuovo cliente'}
                  </Button>
                )}
              </div>

              {!newClientMode ? (
                <div className="relative">
                  <Input
                    placeholder="Cerca cliente per nome o codice fiscale..."
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                    onFocus={() => setShowClientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                    disabled={!!editingRecord}
                  />
                  {showClientDropdown && !editingRecord && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                      {filteredClients.length === 0 ? (
                        <div className="p-3 text-center text-slate-500 text-sm">Nessun cliente trovato</div>
                      ) : (
                        filteredClients.slice(0, 10).map((c) => (
                          <div
                            key={c.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              selectClient(c);
                              setClientSearch(`${c.last_name} ${c.first_name}`);
                              setShowClientDropdown(false);
                            }}
                            className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${formData.client_id === c.id ? 'bg-blue-50 border-blue-200' : ''}`}
                          >
                            <div className="font-medium">{c.last_name} {c.first_name}</div>
                            <div className="text-sm text-slate-500 font-mono">{c.fiscal_code}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {formData.client_id && <p className="text-sm text-green-600 mt-2">Cliente selezionato</p>}
                </div>
              ) : (
                <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                  <p className="text-xs text-slate-500">
                    Il cliente non è ancora in anagrafica: compila i dati per registrarlo — verrà creato
                    al salvataggio della pratica.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Cognome</Label>
                      <Input value={newClientData.last_name}
                        onChange={(e) => setNewClientData({ ...newClientData, last_name: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Nome</Label>
                      <Input value={newClientData.first_name}
                        onChange={(e) => setNewClientData({ ...newClientData, first_name: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Sesso</Label>
                      <Select value={newClientData.gender} onValueChange={(v) => setNewClientData({ ...newClientData, gender: v })}>
                        <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M">Maschio</SelectItem>
                          <SelectItem value="F">Femmina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Data di nascita</Label>
                      <DateInput value={newClientData.birth_date}
                        onChange={(val) => setNewClientData({ ...newClientData, birth_date: val })}
                        placeholder="gg/mm/aaaa" />
                    </div>
                  </div>

                  <div className="p-3 bg-white border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-500">Nato/a all'estero</Label>
                      <Switch
                        checked={newClientData.birth_abroad}
                        onCheckedChange={(checked) => setNewClientData({
                          ...newClientData, birth_abroad: checked, birth_comune: '', birth_provincia: '', birth_country: checked ? 'Cina' : '',
                        })}
                      />
                    </div>
                    {!newClientData.birth_abroad ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-500">Comune di nascita</Label>
                          <Input value={newClientData.birth_comune}
                            onChange={(e) => setNewClientData({ ...newClientData, birth_comune: e.target.value.toUpperCase() })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-500">Provincia (sigla)</Label>
                          <Input value={newClientData.birth_provincia} maxLength={2} className="uppercase"
                            onChange={(e) => setNewClientData({ ...newClientData, birth_provincia: e.target.value.toUpperCase() })} />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Nazione di nascita</Label>
                        <Select value={newClientData.birth_country} onValueChange={(v) => setNewClientData({ ...newClientData, birth_country: v })}>
                          <SelectTrigger><SelectValue placeholder="Seleziona nazione" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {NAZIONI.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Codice fiscale</Label>
                    <div className="flex gap-2">
                      <Input value={newClientData.fiscal_code} className="uppercase"
                        onChange={(e) => setNewClientData({ ...newClientData, fiscal_code: e.target.value.toUpperCase() })} />
                      <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfDaDati}>Calcola CF</Button>
                      <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfNewClient} title="Calcola sesso e data di nascita dal CF">
                        <Calculator className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Indirizzo <span className="text-blue-600">(ricerca Google Maps)</span></Label>
                      {GOOGLE_MAPS_API_KEY ? (
                        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                          <AddressAutocomplete
                            value={newClientData.address}
                            defaultValue={newClientData.address}
                            placeholder="es. Via Roma..."
                            onPlaceSelect={(d) => setNewClientData(prev => ({
                              ...prev,
                              address: (d.address || prev.address || '').toUpperCase(),
                              address_number: (d.address_number || prev.address_number || '').toUpperCase(),
                              city: (d.city || prev.city || '').toUpperCase(),
                              city_provincia: (d.city_provincia || prev.city_provincia || '').toUpperCase(),
                              postal_code: d.postal_code || prev.postal_code,
                            }))}
                          />
                        </APIProvider>
                      ) : (
                        <Input value={newClientData.address}
                          onChange={(e) => setNewClientData({ ...newClientData, address: e.target.value.toUpperCase() })} />
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Numero civico</Label>
                      <Input value={newClientData.address_number}
                        onChange={(e) => setNewClientData({ ...newClientData, address_number: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs text-slate-500">Città di residenza</Label>
                      <Input value={newClientData.city}
                        onChange={(e) => setNewClientData({ ...newClientData, city: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Provincia</Label>
                      <Input value={newClientData.city_provincia} maxLength={2} className="uppercase"
                        onChange={(e) => setNewClientData({ ...newClientData, city_provincia: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">CAP</Label>
                      <Input value={newClientData.postal_code}
                        onChange={(e) => setNewClientData({ ...newClientData, postal_code: e.target.value })} />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs text-slate-500">Cellulare (va sulla domanda)</Label>
                      <Input value={newClientData.phone}
                        onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input type="email" value={newClientData.email}
                      onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value.toUpperCase() })} />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Documento d'identità</Label>
                <Select value={formData.doc_tipo} onValueChange={(v) => set('doc_tipo', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cittadinanza</Label>
                <Input value={formData.cittadinanza} onChange={(e) => set('cittadinanza', e.target.value.toUpperCase())} />
              </div>
              <div className="space-y-2">
                <Label>Numero documento</Label>
                <Input value={formData.doc_numero} onChange={(e) => set('doc_numero', e.target.value.toUpperCase())} />
              </div>
              <div className="space-y-2">
                <Label>Rilasciato da</Label>
                <Input value={formData.doc_ente} onChange={(e) => set('doc_ente', e.target.value.toUpperCase())} />
              </div>
              <div className="col-span-2 flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>Cittadina extracomunitaria</Label>
                  <p className="text-xs text-slate-500">Il permesso di soggiorno (in anagrafica come "PSE ...") diventa allegato obbligatorio della PEC</p>
                </div>
                <Switch checked={formData.extracomunitaria} onCheckedChange={(v) => set('extracomunitaria', v)} />
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <h3 className="font-semibold text-slate-800">Domanda</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>In qualità di</Label>
                  <Select value={formData.qualita} onValueChange={(v) => set('qualita', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {QUALITA_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Bambino/a (nome e cognome)</Label>
                  <Input value={formData.bambino_nome} onChange={(e) => set('bambino_nome', e.target.value.toUpperCase())} />
                </div>
                <div className="space-y-2">
                  <Label>Nato/a il</Label>
                  <DateInput value={formData.bambino_data_nascita} onChange={(v) => set('bambino_data_nascita', v)} placeholder="gg/mm/aaaa" />
                </div>
              </div>

              <Label className="block pt-1">Dichiara</Label>
              <div className="space-y-3 rounded-lg border p-3">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox checked={formData.dich_nessun_trattamento}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, dich_nessun_trattamento: !!v, dich_trattamento_inferiore: v ? false : prev.dich_trattamento_inferiore }))} />
                  di non ricevere alcun trattamento economico della maternità
                </label>
                <div>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox checked={formData.dich_trattamento_inferiore}
                      onCheckedChange={(v) => setFormData(prev => ({ ...prev, dich_trattamento_inferiore: !!v, dich_nessun_trattamento: v ? false : prev.dich_nessun_trattamento }))} />
                    di essere beneficiaria di trattamento economico di maternità inferiore all'importo dell'assegno
                  </label>
                  {formData.dich_trattamento_inferiore && (
                    <div className="grid grid-cols-2 gap-3 mt-2 ml-6">
                      <Input placeholder="Erogato da (ente)" value={formData.trattamento_ente} onChange={(e) => set('trattamento_ente', e.target.value.toUpperCase())} />
                      <Input placeholder="Importo €" value={formData.trattamento_importo} onChange={(e) => set('trattamento_importo', e.target.value)} />
                    </div>
                  )}
                </div>
                <div>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox checked={formData.dich_richiesta_presentata} onCheckedChange={(v) => set('dich_richiesta_presentata', !!v)} />
                    di aver presentato richiesta di prestazione di maternità e di non aver ancora ricevuto comunicazioni
                  </label>
                  {formData.dich_richiesta_presentata && (
                    <Input className="mt-2 ml-6 w-[calc(100%-1.5rem)]" placeholder="Presentata a (ente, es. INPS)" value={formData.richiesta_ente}
                      onChange={(e) => set('richiesta_ente', e.target.value.toUpperCase())} />
                  )}
                </div>
                <div>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox checked={formData.dich_cessato_lavoro} onCheckedChange={(v) => set('dich_cessato_lavoro', !!v)} />
                    di aver cessato lo svolgimento della prestazione lavorativa
                  </label>
                  {formData.dich_cessato_lavoro && (
                    <div className="grid grid-cols-3 gap-3 mt-2 ml-6">
                      <DateInput value={formData.cessazione_data} onChange={(v) => set('cessazione_data', v)} placeholder="Data cessazione" />
                      <Input placeholder="Datore di lavoro" value={formData.datore_lavoro} onChange={(e) => set('datore_lavoro', e.target.value.toUpperCase())} />
                      <Input placeholder="Qualifica" value={formData.qualifica} onChange={(e) => set('qualifica', e.target.value.toUpperCase())} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t pt-4 space-y-3">
              <h3 className="font-semibold text-slate-800">Pagamento</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Modalità di accredito</Label>
                  <Select value={formData.pagamento} onValueChange={(v) => set('pagamento', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAGAMENTO_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input value={formData.iban} maxLength={34} className="font-mono"
                    onChange={(e) => set('iban', e.target.value.replace(/\s+/g, '').toUpperCase())} />
                  {formData.iban && formData.iban.startsWith('IT') && formData.iban.length !== 27 && (
                    <p className="text-xs text-amber-600">Un IBAN italiano ha 27 caratteri (ora {formData.iban.length})</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Istituto bancario / Poste</Label>
                  <Input value={formData.istituto} onChange={(e) => set('istituto', e.target.value.toUpperCase())} />
                </div>
                <div className="space-y-2">
                  <Label>Filiale</Label>
                  <Input value={formData.filiale} onChange={(e) => set('filiale', e.target.value.toUpperCase())} />
                </div>
                <div className="space-y-2">
                  <Label>Intestatario conto</Label>
                  <Input placeholder="vuoto = il richiedente" value={formData.intestatario_conto}
                    onChange={(e) => set('intestatario_conto', e.target.value.toUpperCase())} />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Checkbox checked={formData.conto_cointestato} onCheckedChange={(v) => set('conto_cointestato', !!v)} />
                    Conto cointestato con
                  </Label>
                  <Input disabled={!formData.conto_cointestato} placeholder="Nome e cognome cointestatario" value={formData.cointestatario_nome}
                    onChange={(e) => set('cointestatario_nome', e.target.value.toUpperCase())} />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data della domanda</Label>
                <Input type="date" value={formData.data_domanda} onChange={(e) => set('data_domanda', e.target.value)} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Note</Label>
                <Textarea rows={3} value={formData.notes} onChange={(e) => set('notes', e.target.value)} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving || creatingClient}>
                {creatingClient ? 'Registrazione cliente...' : saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Conferma invio PEC */}
      <Dialog open={!!sendPecConfirm} onOpenChange={(open) => { if (!open) setSendPecConfirm(null); }}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-600" />
              Invia PEC al Comune
            </DialogTitle>
            <DialogDescription>
              Domanda di assegno di maternità{sendPecConfirm ? ` di ${sendPecConfirm.client_name}` : ''}.
              Allegati: domanda firmata (sotto in anteprima), documento d'identità, attestazione ISEE
              {sendPecConfirm?.pse_doc_id ? ', permesso di soggiorno' : ''}.
            </DialogDescription>
          </DialogHeader>
          {sendPecConfirm?.allegati_mancanti?.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              Non si può ancora inviare: manca {sendPecConfirm.allegati_mancanti.join(', ')}.
            </div>
          )}
          <div className="border rounded-lg bg-slate-100 h-[55vh] flex items-center justify-center overflow-hidden">
            {pecPreview.loading ? (
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            ) : pecPreview.url ? (
              <iframe title="Anteprima domanda firmata" src={pecPreview.url} className="w-full h-full bg-white" />
            ) : (
              <p className="text-sm text-slate-500">{pecPreview.error || 'Anteprima non disponibile'}</p>
            )}
          </div>
          <div className="flex items-center gap-2 py-2">
            <Checkbox id="asm-send-copy" checked={sendCopyToClient} onCheckedChange={(v) => setSendCopyToClient(!!v)} />
            <Label htmlFor="asm-send-copy" className="cursor-pointer font-normal">
              Invia anche una copia email al cliente (con la domanda in allegato)
            </Label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setSendPecConfirm(null)}>Annulla</Button>
            <Button
              onClick={() => { const r = sendPecConfirm; setSendPecConfirm(null); handleSendPec(r); }}
              disabled={sendingPec === sendPecConfirm?.id || sendPecConfirm?.allegati_mancanti?.length > 0}
            >
              <Send className="w-4 h-4 mr-2" />Invia PEC
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Configurazione PEC */}
      <Dialog open={pecDialogOpen} onOpenChange={setPecDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-violet-600" />
              Configurazione PEC Assegno Maternità
            </DialogTitle>
            <DialogDescription>
              Casella PEC che invia le domande al Comune (se non modificata è la stessa di Ospitalità).
              Solo gli amministratori possono salvare.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2 space-y-1">
              <Label>Indirizzo PEC</Label>
              <Input type="email" value={pecConfig.pec_email} onChange={(e) => setPecConfig(c => ({ ...c, pec_email: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Password PEC {pecPasswordSet ? '(lascia vuoto per non cambiare)' : ''}</Label>
              <Input type="password" placeholder={pecPasswordSet ? '••••••••' : 'Password della casella PEC'}
                value={pecConfig.pec_password} onChange={(e) => setPecConfig(c => ({ ...c, pec_password: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Server SMTP</Label>
              <Input value={pecConfig.smtp_server} onChange={(e) => setPecConfig(c => ({ ...c, smtp_server: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Porta SMTP</Label>
              <Input type="number" value={pecConfig.smtp_port} onChange={(e) => setPecConfig(c => ({ ...c, smtp_port: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Server IMAP (ricevute)</Label>
              <Input value={pecConfig.imap_server} onChange={(e) => setPecConfig(c => ({ ...c, imap_server: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Porta IMAP</Label>
              <Input type="number" value={pecConfig.imap_port} onChange={(e) => setPecConfig(c => ({ ...c, imap_port: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Destinatario (Comune)</Label>
              <Input type="email" value={pecConfig.default_recipient} onChange={(e) => setPecConfig(c => ({ ...c, default_recipient: e.target.value }))} />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>Invia email di test a</Label>
              <div className="flex gap-2">
                <Input type="email" value={pecTestRecipient} onChange={(e) => setPecTestRecipient(e.target.value)} />
                <Button type="button" variant="outline" onClick={testPecConfig} disabled={pecTesting || !pecTestRecipient}>
                  {pecTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                </Button>
              </div>
              <p className="text-xs text-slate-500">Il test usa la configurazione salvata.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPecDialogOpen(false)}>Chiudi</Button>
            <Button onClick={savePecConfig} disabled={pecSaving}>{pecSaving ? 'Salvataggio...' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssegnoMaternita;
